/**
 * SearchContentTagItem Component
 * Individual content tag row in search results
 * Matches Flutter's search_screen.dart contentItem
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import type { ContentTagEntity } from '@/domain/entities/contentTag/contentTagEntity';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface SearchContentTagItemProps {
  contentTag: ContentTagEntity;
}

export const SearchContentTagItem: React.FC<SearchContentTagItemProps> = ({ contentTag }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const isHashTag = contentTag.isHashtag === true;

  const usesText = contentTag.uses === 1
    ? `1 ${l10n.search.use}`
    : `${contentTag.uses} ${l10n.search.uses}`;

  return (
    <div
      {...qaAttr({ id: `search-result-tag-${contentTag.name}` })}
      style={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        paddingBottom: responsive.spacing.lg,
      }}
    >
      {/* Icon circle */}
      <div
        style={{
          width: responsive.avatar.medium,
          height: responsive.avatar.medium,
          borderRadius: responsive.borderRadius.round,
          backgroundColor: theme.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <AssetIcon
          path={isHashTag ? '/assets/images/hashtag.png' : '/assets/images/label.png'}
          width={responsive.avatar.medium / 2}
          height={responsive.avatar.medium / 2}
          color={theme.text.secondary}
        />
      </div>

      <div style={{ flex: 1, marginLeft: responsive.spacing.base }}>
        <p
          style={{
            fontWeight: responsive.fontWeight.semibold,
            color: theme.text.primary,
            fontSize: responsive.font.base,
            margin: 0,
          }}
        >
          {contentTag.name}
        </p>
        <p
          style={{
            fontSize: responsive.font.sm,
            color: theme.text.secondary,
            margin: 0,
            marginTop: responsive.spacing.xs,
          }}
        >
          {usesText}
        </p>
      </div>
    </div>
  );
};
